import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { tokenService } from '@/services/tokenService'
import { finalizeLocalSessionEnd } from '@/lib/sessionCleanup'
import { BASE_URL, API_KEY, CHANNEL_TYPE, DEBUG_MODE } from '@/config/api'

// Create axios instance
const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: 60000, // 60 seconds timeout
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json, text/plain, */*',
  },
})

// Track if refresh is in progress
let isRefreshing = false
let refreshSubscribers: Array<(token: string) => void> = []

const PUBLIC_ENDPOINTS = [
  '/api/auth/begin/',
  '/api/auth/verify/',
  '/api/auth/token/refresh/',
  '/api/users/register/',
  '/api/surveys/registration/',
]

const isPublicEndpoint = (url?: string): boolean => {
  if (!url) return false
  // Submit may be called anonymously (login/register) or with JWT (dashboard add-provider).
  if (url.includes('/api/surveys/registration/submit/')) return false
  return PUBLIC_ENDPOINTS.some((ep) => url.includes(ep))
}

// Request interceptor
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    config.headers.set('X-WTD-Channel', CHANNEL_TYPE)

    if (API_KEY) {
      config.headers.set('X-API-Key', API_KEY)
    }

    if (!isPublicEndpoint(config.url)) {
      const token = await tokenService.getAccessToken()
      if (token) {
        config.headers.set('Authorization', `Bearer ${token}`)
      }
    }

    if (DEBUG_MODE) {
      console.log('[API] Request:', {
        url: config.url,
        method: config.method,
        headers: {
          ...config.headers.toJSON(),
          Authorization: config.headers.get('Authorization') ? 'Bearer ***' : undefined,
        },
      })
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Token refresh function
const attemptTokenRefresh = async (
  attemptNumber = 1,
  maxAttempts = 3
): Promise<{ success: boolean; accessToken?: string }> => {
  if (attemptNumber > maxAttempts) {
    return { success: false }
  }

  const refreshToken = await tokenService.getRefreshToken()
  if (!refreshToken) {
    return { success: false }
  }

  try {
    const response = await axios.post(
      `${BASE_URL}/api/auth/token/refresh/`,
      { refresh: refreshToken },
      {
        headers: {
          'X-API-Key': API_KEY,
          'X-WTD-Channel': CHANNEL_TYPE,
          'Content-Type': 'application/json',
        },
        timeout: 10000,
      }
    )

    const { access, refresh: newRefresh } = response.data
    await tokenService.saveTokens(access, newRefresh ?? refreshToken)

    if (DEBUG_MODE) {
      console.log('[API] Token refresh successful')
    }

    return { success: true, accessToken: access }
  } catch (error) {
    // Retry for network errors, fail for invalid token
    if (attemptNumber < maxAttempts && axios.isAxiosError(error)) {
      const axiosError = error as AxiosError
      if (
        axiosError.code === 'ECONNABORTED' ||
        axiosError.code === 'ERR_NETWORK' ||
        (axiosError.response && axiosError.response.status >= 500)
      ) {
        return attemptTokenRefresh(attemptNumber + 1, maxAttempts)
      }
    }
    return { success: false }
  }
}

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    if (DEBUG_MODE) {
      console.log('[API] Response:', {
        url: response.config.url,
        status: response.status,
      })
    }
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean
    }

    // Check if error is due to token expiration
    const isTokenError =
      error.response?.status === 401 ||
      (error.response?.data as any)?.code === 'token_not_valid'

    // Don't redirect during login/auth flow - only redirect for authenticated endpoints
    const isAuthEndpoint = originalRequest?.url?.includes('/api/auth/begin/') || 
                          originalRequest?.url?.includes('/api/auth/verify/') ||
                          originalRequest?.url?.includes('/api/auth/select-strategy/')
    
    if (isTokenError && originalRequest && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        // Wait for refresh to complete
        return new Promise((resolve) => {
          refreshSubscribers.push((token: string) => {
            originalRequest.headers.set('Authorization', `Bearer ${token}`)
            resolve(apiClient(originalRequest))
          })
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      const refreshResult = await attemptTokenRefresh()

      if (refreshResult.success && refreshResult.accessToken) {
        // Notify all waiting requests
        refreshSubscribers.forEach((callback) =>
          callback(refreshResult.accessToken!)
        )
        refreshSubscribers = []

        // Retry original request
        originalRequest.headers.set(
          'Authorization',
          `Bearer ${refreshResult.accessToken}`
        )
        isRefreshing = false
        return apiClient(originalRequest)
      } else {
        // Refresh failed, logout user
        isRefreshing = false
        refreshSubscribers = []

        await finalizeLocalSessionEnd('token_expired')

        // Redirect to login
        window.location.href = '/login'
        return Promise.reject(error)
      }
    }

    if (DEBUG_MODE) {
      console.error('[API] Error:', {
        url: originalRequest?.url,
        status: error.response?.status,
        message: error.message,
      })
    }

    return Promise.reject(error)
  }
)

export default apiClient
